import { CSSProperties } from "react";
import {
  PartialBrandTheme,
  PartialQuestThemeData,
  StyleConfig,
  ThemeConfig,
} from "./getStarted.types";
import { extractGradientColors, hexToGlassColor } from "../../../../general";

class ThemeManager {
  themeConfig: ThemeConfig;
  styleConfig: StyleConfig;
  questThemeData: PartialQuestThemeData;
  brandTheme: PartialBrandTheme;

  constructor(
    themeConfig: ThemeConfig = {},
    styleConfig: StyleConfig = {},
    questThemeData: PartialQuestThemeData = {
      accentColor: "",
      theme: "",
      borderRadius: "",
      buttonColor: "",
      images: [],
    },
    brandTheme: PartialBrandTheme = {
      accentColor: "",
      background: "",
      borderRadius: "",
      buttonColor: "",
      contentColor: "",
      fontFamily: "",
      logo: "",
      primaryColor: "",
      secondaryColor: "",
      tertiaryColor: "",
      titleColor: "",
    }
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = questThemeData;
    this.brandTheme = brandTheme;
  }

  getStyleValueAsString(path: string, defaultValue: string = ""): string {
    const keys = path.split(".");
    let value: any = this.styleConfig;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return typeof value === "string" ? value : defaultValue;
  }

  getStyleValueAsObject<T>(path: string, defaultValue: T): T {
    const keys = path.split(".");
    let value: any = this.styleConfig;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return (typeof value === "object" ? value : defaultValue) as T;
  }

  getFormStyles(): CSSProperties {
    const baseStyles: CSSProperties = {
      background: this.getBackgroundColor(),
      fontFamily: this.getFontFamily(),
      borderRadius: this.getBorderRadius(),
      height: this.getStyleValueAsString("Form.height", "auto"),
    };

    const formStyles = this.styleConfig?.Form || {};
    return { ...baseStyles, ...formStyles };
  }

  getCardStyles(template = 1, imageUrl?: string): {
    container: CSSProperties;
    card: CSSProperties;
    icon: CSSProperties;
  } {
    const defaultGap = template === 2 ? "0px" : "16px";
    const defaultPadding = template === 2 ? "0px 20px 20px" : "0px 20px 20px";

    return {
      container: {
        padding: this.getStyleValueAsString(
          "CardContainer.padding",
          defaultPadding
        ),
        gap: this.getStyleValueAsString("CardContainer.gap", defaultGap),
        ...(this.styleConfig?.CardContainer || {}),
      },
      card: this.styleConfig?.Card || {},
      icon: { ...this.styleConfig?.Icon, background: imageUrl ? this?.styleConfig?.Icon?.background || hexToGlassColor(this?.brandTheme?.primaryColor || this?.questThemeData?.accentColor || '#f4ebff', (this?.brandTheme?.primaryColor || this?.questThemeData?.accentColor) ? 0.5 : 1) : this?.brandTheme?.primaryColor || this?.questThemeData?.accentColor || '#f4ebff' },
    };
  }

  getHeadingStyles(): CSSProperties {
    return {
      color:
        this.getStyleValueAsString("Heading.color") ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor ||
        "#000000",
      ...(this.styleConfig?.Heading || {}),
    };
  }

  getHeadingTitleStyles(): CSSProperties {
    return {
      color:
        this.getStyleValueAsString("Heading.color") ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...(this.styleConfig?.Heading || {}),
    };
  }

  getDescriptionStyles(): CSSProperties {
    return {
      color:
        this.getStyleValueAsString("Description.color") ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor ||
        "#666666",
      ...(this.styleConfig?.Description || {}),
    };
  }

  getTopbarStyles(): CSSProperties {
    return this.getStyleValueAsObject<CSSProperties>("Topbar", {});
  }

  getFooterStyles(): {
    container: CSSProperties;
    text: CSSProperties;
    icon: CSSProperties;
    link : string
  } {
    return {
      container: {
        background: this.getBackgroundColor(),
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...(this.styleConfig?.Footer?.FooterStyle || {}),
      },
      text: this.styleConfig?.Footer?.FooterText || {},
      icon: this.styleConfig?.Footer?.FooterIcon || {},
      link: this.styleConfig?.Footer?.FooterLink || 'https://www.plgos.com/'
    };
  }

  getProgressBarStyles(): {
    text: CSSProperties;
    bar: { background: string; fill: string };
  } {
    return {
      text: {
        color: this.getStyleValueAsString(
          "ProgressBar.ProgressText.color",
          extractGradientColors(this.brandTheme?.titleColor) || this.themeConfig?.primaryColor ||
          "#9035ff"
        ),
      
        ...this.styleConfig?.ProgressBar?.ProgressText,
      },
      bar: {
        background: this.getStyleValueAsString(
          "ProgressBar.barParentColor",
          "#f0f0f0"
        ),
        fill: this.getStyleValueAsString("ProgressBar.barColor") || this.brandTheme?.primaryColor || this.questThemeData?.accentColor || "#9035ff",
      },
    };
  }

  getButtonStyles(): {
    primary: CSSProperties;
    secondary: CSSProperties;
  } {
    return {
      primary: {
        background:
          this.getStyleValueAsString("PrimaryButton.background") ||
          this.brandTheme?.primaryColor ||
          this.questThemeData?.accentColor ||
          this.questThemeData?.buttonColor ||
          this.themeConfig?.buttonColor,
        ...(this.styleConfig?.PrimaryButton || {}),
      },
      secondary: this.styleConfig?.SecondaryButton || {},
    };
  }

  getArrowStyles(isCompleted: boolean): {
    background: string;
    iconColor: string;
  } {
    const base = this.styleConfig?.Arrow || {};
    return {
      background: isCompleted
        ? base.CompletedBackground || ""
        : base.Background || "",
      iconColor: isCompleted
        ? base.CompletedIconColor || "#098849"
        : base.IconColor || "",
    };
  }

  getBackgroundColor(): string {
    return (
      this.getStyleValueAsString("Form.backgroundColor") ||
      this.getStyleValueAsString("Form.background") ||
      this.brandTheme?.background ||
      this.themeConfig?.backgroundColor
    );
  }

  getFontFamily(): string {
    return (
      this.themeConfig?.fontFamily || this.brandTheme?.fontFamily || "Figtree"
    );
  }

  getBorderRadius(): string {
    return (
      this.getStyleValueAsString("Form.borderRadius") ||
      this.questThemeData?.borderRadius ||
      this.brandTheme?.borderRadius ||
      "8px"
    );
  }

  updateThemeConfig(newConfig: Partial<ThemeConfig>): void {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<StyleConfig>): void {
    this.styleConfig = { ...this.styleConfig, ...newConfig };
  }

  updateQuestTheme(newTheme: Partial<PartialQuestThemeData>): void {
    this.questThemeData = { ...this.questThemeData, ...newTheme };
  }

  updateBrandTheme(newTheme: Partial<PartialBrandTheme>): void {
    this.brandTheme = { ...this.brandTheme, ...newTheme };
  }
}

export default ThemeManager;
